import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { AgentStateData, AgentTodayData, WorkItem } from '../../models/reports.external';
import { defaultAgentReport, IAgentReport, ITeamReport, ReportDictionary } from '../../models/reports.internal';
import { groupBy } from '../../utils/arrayUtils';
import { mapObjectValues, objectValues } from '../../utils/objectUtils';
import { SignalrDataContext } from '../../providers/SignalrDataProvider';
import { TemporaryDataCacheContext } from '../../providers/TemporaryDataCacheProvider';
import useLazyUpdateState from '../../shared/hooks/useLazyUpdateState';
import { processAgentState, processAgentToday, processWorkItem } from '../../calculations/agentCalculations';
import aggregateAgentsIntoTeam from '../../calculations/aggregateAgentsIntoTeam';
import toReportDictionary from '../../calculations/toReportDictionary';
import { ReportDataState } from '../../components/shared/ReportList';

const cacheKey = 'useGetTeamReports-agentsDict';

function get(dictionary: ReportDictionary<IAgentReport>, id: string): IAgentReport {
  return dictionary[id] ?? defaultAgentReport(id);
}

export default function useGetTeamReports(): ReportDataState<ITeamReport> {
  const { retrieveData, storeData } = useContext(TemporaryDataCacheContext);
  const { subscribe, unsubscribe, myTeams } = useContext(SignalrDataContext);

  const [agentsDict, setAgentsDict] = useLazyUpdateState<ReportDictionary<IAgentReport>>({});
  const agentsDictRef = useRef(agentsDict);
  agentsDictRef.current = agentsDict;

  //#region [1] Retrieve from and store to cache
  useEffect(
    () => {
      const cachedAgents = retrieveData<ReportDictionary<IAgentReport>>(cacheKey);
      
      if (cachedAgents !== undefined)
        setAgentsDict({ ...cachedAgents });

      return () => storeData(cacheKey, agentsDictRef.current, 10);
    },
    [retrieveData, setAgentsDict, storeData],
  );
  //#endregion
  
  //#region [2] aggregate updated data into agentsDict
  const receiveTodaysAgentData = useCallback(
    (data: AgentTodayData) => setAgentsDict(dict => {
      const agent = get(dict, data.id);
      const processed = processAgentToday(data);
      const merged: IAgentReport = { ...agent, ...processed };

      return { ...dict, [merged.id]: merged };
    }),
    [setAgentsDict],
  );

  const receiveAgentStates = useCallback(
    (data: AgentStateData[]) => setAgentsDict(dict => {
      const updatedReports = data.map(a => {
        const agent = get(dict, a.agentId);
        const processed = processAgentState(a);
        return { ...agent, ...processed };
      });

      const updatedReportsDict = toReportDictionary(updatedReports);
      return { ...dict, ...updatedReportsDict };
    }),
    [setAgentsDict],
  );

  const receiveWorkItems = useCallback(
    (data: WorkItem[]) => {
      const groupedWorkItems = groupBy(data, wi => wi.agentId);
      const processedGroups = mapObjectValues(groupedWorkItems, (_, wIs) => wIs.map(processWorkItem));

      setAgentsDict(dict =>
        mapObjectValues(
          dict,
          (_, a) => ({ ...a, currentlyHandledWorkItems: processedGroups[a.id] ?? [] }),
        ),
      );
    },
    [setAgentsDict],
  );
  //#endregion

  //#region [3] Manage subscription
  useEffect(
    () => {
      const subId = subscribe({ receiveTodaysAgentData, receiveAgentStates, receiveWorkItems });
      return () => unsubscribe(subId);
    },
    [receiveAgentStates, receiveTodaysAgentData, receiveWorkItems, subscribe, unsubscribe],
  );
  //#endregion

  const teamReports = useMemo<ITeamReport[]>(
    () => myTeams.map(t => aggregateAgentsIntoTeam(t, objectValues(agentsDict))),
    [agentsDict, myTeams],
  );

  const result = useMemo<ReportDataState<ITeamReport>>(
    () => ({
      items: teamReports,
      isLoading: teamReports.length === 0,
      willNotLoad: myTeams.length === 0,
    }),
    [myTeams.length, teamReports],
  );

  return result;
}